<script lang="ts">
  import { onMount } from 'svelte';
  import { createQuery } from '@tanstack/svelte-query';
  import { activitiesApi } from '$lib/api/crm';
  import { session } from '$lib/state/session.svelte';
  import { route } from '$lib/state/route.svelte';
  import { roleProfile, workflowAccessKey } from '$lib/utils/roleProfile';
  import { readQueuePages, todaysMeetings, dayInIndia } from '$lib/utils/myDay';
  import { apiFetch, getErrorMessage } from '$lib/api/client';
  import { timeInIndia } from '$lib/utils/myDay';

  let { today: suppliedToday }: { today?: string } = $props();
  let now = $state(new Date());
  const today = $derived(suppliedToday ?? dayInIndia(now.toISOString())!);
  onMount(() => { const timer = setInterval(() => now = new Date(), 60000); return () => clearInterval(timer); });
  const profile = $derived(roleProfile(session.me));
  const accessKey = $derived(workflowAccessKey(session.me));
  const userId = $derived(session.me?.user.id);
  const vertical = $derived(session.selectedVertical);
  const verticalId = $derived(vertical === 'all' ? undefined : vertical);
  const query = createQuery(() => ({
    queryKey: ['my-day-meetings', profile, userId, vertical, accessKey], enabled: profile === 'coordination',
    queryFn: () => {
      const requestedVertical = verticalId;
      return readQueuePages(page => activitiesApi.list({ type: 'meeting', vertical_id: requestedVertical, page, per_page: 100 }));
    }, staleTime: 60000, refetchOnWindowFocus: true
  }));
  const attendees = createQuery(() => ({
    queryKey: ['my-day-meeting-attendees', profile, userId, vertical, accessKey], enabled: profile === 'coordination',
    queryFn: async () => {
      // This endpoint requires one positive pipeline ID. For All, union only
      // full-CRM grants, never issue-only support teams.
      const ids = [...new Set((session.me?.verticals ?? [])
        .filter(team => Number.isSafeInteger(team.id) && team.id > 0 && (verticalId === undefined || team.id === verticalId))
        .map(team => team.id))];
      const groups = await Promise.all(ids.map(id => apiFetch<Array<{ id: number; name: string }>>(`/activities/assignees?vertical_id=${id}`)));
      return [...new Map(groups.flat().map(person => [person.id, person])).values()];
    }
  }));
  function attendeeName(id: number | null | undefined) {
    return id ? attendees.data?.find(person => person.id === id)?.name || `User #${id}` : 'Unassigned — choose an attendee';
  }
  // Booking actor, contact owner and attendee are distinct: this agenda lists
  // meetings booked by the coordinator's account, whoever attends.
  const meetings = $derived(session.me ? todaysMeetings(query.data ?? [], today, 'booked', session.me.user) : []);
  const needsAttendee = $derived(meetings.filter(meeting => !meeting.assignee_user_id).length);
  function status(meeting: { assignee_user_id?: number | null; scheduled_at: string | null }) {
    if (!meeting.assignee_user_id) return { label: 'Needs attendee', chip: 'day-chip-warn' };
    return Date.parse(meeting.scheduled_at ?? '') < now.getTime() ? { label: 'Started', chip: 'day-chip-ok' } : { label: 'Upcoming', chip: 'day-chip-info' };
  }
</script>

{#if profile === 'coordination'}
  <section class="day-card" aria-labelledby="booked-meetings">
    <div class="flex flex-wrap items-baseline justify-between gap-2">
      <h2 id="booked-meetings" class="day-title">Meetings I booked · today{#if query.isSuccess} · {meetings.length}{/if}</h2>
      <span class="day-meta">You arranged them · the attendee owns them</span>
    </div>
    {#if query.isSuccess && needsAttendee}<p class="mt-2 text-sm font-semibold text-[#8a5300]">{needsAttendee} {needsAttendee === 1 ? 'meeting has' : 'meetings have'} no attendee yet.</p>{/if}
    {#if attendees.isError}<p class="mt-2 text-xs" role="alert">Attendee names unavailable; showing IDs. <button class="underline" onclick={() => attendees.refetch()}>Retry names</button></p>{/if}
    {#if query.isPending}<p role="status" class="day-meta mt-3">Loading today's agenda…</p>
    {:else if query.isError}<p role="alert" class="mt-3 text-sm text-red-700">{getErrorMessage(query.error)}</p><button class="day-mini-btn mt-2" onclick={() => query.refetch()}>Retry meetings</button>
    {:else if meetings.length}
      <div class="mt-3 overflow-x-auto">
        <table class="w-full min-w-[460px] border-collapse text-sm">
          <thead><tr class="text-left text-xs text-dark-gray"><th class="px-1.5 py-2 font-medium">Time</th><th class="px-1.5 py-2 font-medium">Meeting</th><th class="px-1.5 py-2 font-medium">Attendee</th><th class="px-1.5 py-2 font-medium">Status</th></tr></thead>
          <tbody>
            {#each meetings.slice(0, 8) as meeting (meeting.id)}
              {@const state = status(meeting)}
              <tr class="border-t border-[#eeedea] hover:bg-[#fafaf8]">
                <td class="px-1.5 py-2.5 font-semibold">{timeInIndia(meeting.scheduled_at)}</td>
                <td class="px-1.5 py-2.5"><button class="text-left font-semibold text-navy hover:underline" onclick={() => route.push({ tab: 'activities', id: meeting.id })}>{meeting.title || 'Meeting'}</button>{#if meeting.location}<span class="block text-xs text-dark-gray">{meeting.location}</span>{/if}</td>
                <td class="px-1.5 py-2.5">{#if meeting.assignee_user_id}{attendeeName(meeting.assignee_user_id)}{:else}<button class="rounded-full border border-dashed border-[#8a5300] bg-[#fff1db] px-2.5 py-1 text-xs font-semibold text-[#8a5300]" onclick={() => route.push({ tab: 'activities', id: meeting.id })}>Choose attendee</button>{/if}</td>
                <td class="px-1.5 py-2.5"><span class="day-chip {state.chip}">{state.label}</span></td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
      {#if meetings.length > 8}<p class="mt-2 text-xs text-dark-gray">Showing 8 of {meetings.length} meetings today.</p>{/if}
    {:else}<p class="mt-3 rounded-xl bg-[#f7f7f4] p-3 text-sm text-dark-gray">No meetings booked by you for today in this scope.</p>{/if}
  </section>
{/if}
